/**
 *
 * CommunicationListItem
 *
 */

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from "react-i18next";

// icons
import CustomIcons from 'components/CustomIcons';

// antd component
import { List, Tooltip } from 'antd';

import {
  getCommunicationStatusForFormAppoint,
  getCommunicationItemStatus,
  getCommunicationItemStatusClass,
  getCommunicationType,
  getCommunicationTypeLabel,
  getCommunicationAudienceAsString,
} from 'utils/helpers/helper.generateData';

// utils
import { getObjId } from 'utils/helpers/helper.general';
import { convertDateToString, convertDateTimeToString } from 'utils/moment/moment.utils';
import _local from 'utils/localStorageUtils';

function CommunicationListItem({
  element,
  activeEl,
  searchString,
  // formats
  timeFormat,
  dateFormat,
  dateTimeFormat,
  // func
  customHighlighter,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    'communication-list-meta',
    className
  );
  const { t, i18n } = useTranslation();

  const localUser = _local.getLocalUser();
  const senderRole = element?.sender?.[0]?.role || 'admin';

  const date = `${(element?.filterDate ? convertDateTimeToString(element?.filterDate) : convertDateTimeToString(element?.creationDate))}`;
  const deadlineDate = `${element?.deadlineDate ? convertDateToString(element?.deadlineDate) : ''}`;

  const audienceStrFull = getCommunicationAudienceAsString(element);
  
  const isShowFormMark = useCallback((element, activeEl) => {
    const key = "forms";
    let compareEl = element;
    if (activeEl?.readershipFormsFiltered) {
      compareEl = {
        ...compareEl,
        readershipFormsFiltered: activeEl?.readershipFormsFiltered
      }
    }

    const hasType = compareEl?.types?.filter(el => el == key);
    const hasForm = hasType?.length > 0;
    const hasNew = !!compareEl?.readershipFormsFiltered && compareEl?.readershipFormsFiltered?.some(el => !!el?.hasNew) || !!compareEl?.readershipFormsFiltered && compareEl?.readershipFormsFiltered?.length == 0;
    return !!compareEl?.latestFormAnswer && (!!hasForm && !!hasNew);
  }, []);

  const isShowAppointmentMark = useCallback((element, activeEl) => {
    const key = "appointments";
    let compareEl = element;
    if (activeEl?.readershipAppointmentsFiltered) {
      compareEl = {
        ...compareEl,
        readershipAppointmentsFiltered: activeEl?.readershipAppointmentsFiltered
      }
    }

    const hasType = compareEl?.types?.filter(el => el == key);
    const hasAppointment = hasType?.length > 0;
    const hasNew = !!compareEl?.readershipAppointmentsFiltered && compareEl?.readershipAppointmentsFiltered?.some(el => !!el?.hasNew) || !!compareEl?.readershipAppointmentsFiltered && compareEl?.readershipAppointmentsFiltered?.length == 0;
    return !!compareEl?.latestAppointmentAnswer && (!!hasAppointment && !!hasNew)
  }, []);

  const isShowChat = useCallback((compareEl) => {
    const key = "discussion";
    const hasType = compareEl?.types?.filter(el => el == key);
    const hasDiscussion = hasType?.length > 0;
    const hasAsReaders = compareEl?.readers?.filter(el => (
      (el?.user == localUser?._id)
    ));
    const readers = compareEl?.readersFiltered?.filter(el => (
      (el?.reader?.user == localUser?._id)
    ));
    const hasNewMessages = readers?.some(el => !!el?.hasNewMessages) || hasAsReaders?.length == 0;
    return !!hasDiscussion && !!hasNewMessages;
  }, [localUser?._id]);

  const [marks, setMarks] = useState({
    forms: isShowFormMark(element),
    appointments: isShowAppointmentMark(element),
    chat: isShowChat(element)
  });

  useEffect(() => {
    if (!!activeEl && getObjId(element) == getObjId(activeEl)) {
      const compareEl = {
        ...element,
        ...activeEl
      }
      const newMarks = {
        forms: isShowFormMark(element, activeEl),
        appointments: isShowAppointmentMark(element, activeEl),
        chat: isShowChat(compareEl)
      };
      //console.log('-----', {newMarks, element, activeEl})
      setMarks(newMarks);
    }
  }, [activeEl, activeEl?.readers]);

  const MarkWrapper = ({ key, title, color, trigger, children }) => {
    return !!title ? (
      <Tooltip
        key={key}
        placement="top"
        title={title}
        color={!!color ? color : "#005D9F"}
        trigger={trigger || ["hover"]}
      >
        {children}
      </Tooltip>
    ) : <>{children}</>
  };

  const renderItemTitle = useMemo(() => {
    return (
      <>
        <span className='sub-title'>
          <span className="date">
            {customHighlighter(`${date}`, searchString)}
          </span> 
          {!(senderRole == 'parent') && (
            <>
              {element?.status == 'draft' ? (
                <span className={`status ${getCommunicationItemStatusClass(element)}`}>
                  {getCommunicationItemStatus(element)}
                </span>
              ) : !!deadlineDate && (
                <>
                  <span className="date deadline">
                    {customHighlighter(`${t("inputs.deadline")}: ${deadlineDate}`, searchString)}
                  </span>
                </>
              )}
            </>

          )}
        </span>
      </>
    )
  }, [element, deadlineDate, senderRole, date, searchString, i18n?.language]);

  const renderItemBody = useMemo(() => { 
    return (
      <>
        <span className="title">{customHighlighter(element?.title, searchString)}</span>
        {element?.audience && !(senderRole == 'parent') && (
          <span className='audience'>{customHighlighter(audienceStrFull, searchString)}</span>
        )}
        {element?.reason && senderRole == 'parent' && (
          <span className='reason'>{customHighlighter(element?.reason, searchString)}</span>
        )}
        {element?.types && (
          <span className='tag-types'>
            {getCommunicationType(element)?.map((typeItem, i) => typeItem !== 'no-type' && (
              <span key={typeItem + '-type-' + i} className={`tag-type tag-type--${typeItem}`}>{customHighlighter(getCommunicationTypeLabel(typeItem)?.label, searchString)}</span>
            ))}
          </span>
        )}
        {element?.status == 'sent' && (
          <span className="marks">
            {marks?.appointments && (
              <MarkWrapper
                key={`appointment-mark-${element?._id}`}
                title={getCommunicationStatusForFormAppoint(element)}
                color="#fa6400"
              >
                <span className="mark appointment-mark"><CustomIcons type="calendar" /></span>
              </MarkWrapper>
            )}
            {marks?.forms && (
              <MarkWrapper
                key={`form-mark-${element?._id}`}
                title={getCommunicationStatusForFormAppoint(element)}
                color="#f59c05"
              >
                <span className="mark form-mark"><CustomIcons type="document" /></span>
              </MarkWrapper>
            )}
            
            {marks?.chat && (
              <span className="mark discussion-mark"><CustomIcons type="comments" /></span>
            )}
          </span>
        )}
      </>
    )
  }, [element, marks, searchString, i18n?.language]);
  
  return element && (
    <List.Item.Meta
      className={childClassNames}
      style={{ borderLeft: `6px solid ${element?.subject?.color}` }}
      title={renderItemTitle}
      description={renderItemBody}
    />
  );

};

CommunicationListItem.defaultProps = {
  dateFormat: 'DD.MM.YYYY',
  dateTimeFormat: 'DD.MM.YYYY - HH:mm',
  timeFormat: 'HH:mm',
};
CommunicationListItem.propTypes = {
  element: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.bool.isRequired,
  ]),
  searchString: PropTypes.string,
  customHighlighter: PropTypes.func,
};

export default memo(CommunicationListItem);